import { myProject, myListOfProjects } from '@/cruds/project.crud';
import { ApolloClient } from 'apollo-client';
import { version } from '../../../package.json';
import { setContext } from 'apollo-link-context';
import { isNil, isNotNull } from '@/shared/utils';
import { getValidations } from '@/cruds/validations.crud';
import { getAllFormsData, addForm, addLabelToFormContentInDB, getAllMenus, getMenu, setGlobalMenuMap } from '@/cruds/language.crud';
import { myProfile, GetPlanningValidation } from '@/cruds/users.crud';
import { PAI_MANAGER_FUNCTION_ID } from '@/shared/constants';

const AUTH_TOKEN = 'jwt-token';
const CURRENT_PROJECT = 'current-project';
const SAVED_NO_TRANSLATIONS = 'saved-no-trans-menus';

const state = {
	debug: true,
	loaded: false,
	myProfile: {},
	myProject: {},
	myProjects: {},
	noTranslationMenus: {},
	token: null,
	myContracts: [],
	userID: null,
	tokenObject: null,
	isShowFimalacPlanning: false,
	isProduction: false
};
function getTokenProp(state, key) {
	const tokenDecoded = state.tokenObject.TokenData || null;
	if (tokenDecoded) {
		return tokenDecoded[key];
	}
	return false;
}
const getters = {
	isLoaded: (state) => state.loaded,
	getToken: (state) => state.token,
	userID: (state) => state.userID,
	myProject: (state) => state.myProject,
	myProjects: (state) => state.myProjects,
	myContract: (state) => state.myContract,
	myProfile: (state) => state.myProfile,
	tokenObject: (state) => state.tokenObject,
	isShowFimalacPlanning: (state) => state.isShowFimalacPlanning,
	menuMap: (state) => state.menuMap,
	getCurrentProjectConfig: (state) => state.myProject,
	getSavedNoTranslationMenus: (state) => state.noTranslationMenus,
	isFilmSingleUser: (state) => {
		const ProjectType = state.tokenObject.TokenData.ProjectType;
		if (ProjectType) {
			return ProjectType === 4;
		}
		return false;
	},
	isFilmSingleUserFree: (state) => {
		const ProjectType = state.tokenObject.TokenData.ProjectType;
		if (ProjectType) {
			return ProjectType === 3;
		}
		return false;
	},
	isDev: (state) => getTokenProp(state, 'IsDev'),
	canManageCrewList: (state) => getTokenProp(state, 'CanManageCrewList'),
	canManageDocumentPackage: (state) => getTokenProp(state, 'CanManageDocumentPackage'),
	canManageLocations: (state) => getTokenProp(state, 'CanManageLocations'),
	canAccessConfig: (state) => getTokenProp(state, 'CanAccessConfig'),
	isProd: (state) => getTokenProp(state, 'IsProd'),
	isTranslator: (state) => getTokenProp(state, 'IsTranslator'),
	appLanguage: (state) => getTokenProp(state, 'Language'),
	projectID: (state) => getTokenProp(state, 'ProjectID'),
	canExportImport: (state) => getTokenProp(state, 'CanExportImport'),
	isAdmin: (state) => getTokenProp(state, 'IsAdmin'),
	isCarbonClapReporting: (state) => state.myProject.greenReporting === 3,
	isGestionnaryOfPaie: (state) => state.myProfile.function === PAI_MANAGER_FUNCTION_ID,
	isProduction: (state) => state.isProduction,
	isFilm: (state) => {
		const ProjectType = state.tokenObject.TokenData.ProjectType;
		if (ProjectType) {
			return ProjectType === 0;
		}
		return false;
	},
	isPme: (state) => {
		const ProjectType = state.tokenObject.TokenData.ProjectType;
		if (ProjectType) {
			return ProjectType === 1;
		}
		return false;
	}
};
const mutations = {
	SET_MY_PROJECTS(state, value) {
		state.myProjects = value;
	},
	SET_TOKEN(state, value) {
		state.token = value;
	},
	SET_LOADED(state, value) {
		state.loaded = value;
	},
	SET_MY_PROFILE(state, currentProfile) {
		state.myProfile = currentProfile;
	},
	SET_PROJECT(state, currentProject) {
		state.myProject = currentProject;
	},
	SET_NO_TRANSLATION(state, value) {
		state.noTranslationMenus = value;
	},
	SET_USER_ID(state, value) {
		state.userID = value;
	},
	SET_TOKEN_OBJECT(state, value) {
		state.tokenObject = value;
	},
	SET_FIMALAC(state, value) {
		state.isShowFimalacPlanning = value;
	},
	SET_MENU_MAPS(state, value) {
		state.menuMap = value;
	},
	SET_IS_PROD(state, value) {
		state.isProduction = value;
	}
};
const actions = {
	jwtData({ commit, dispatch, state }) {
		const token = state.token;
		if (!isNil(token) && token.length > 0) {
			const str = token.split('.')[1];
			const strToStringBase64 = Buffer.from(str, 'base64').toString('binary');
			const response = JSON.parse(strToStringBase64);
			if (response) {
				const { TokenData } = response;
				commit('SET_USER_ID', TokenData.UserID);
				commit('SET_TOKEN_OBJECT', response);
			}
		} else {
			commit('SET_USER_ID', null);
			commit('SET_TOKEN_OBJECT', null);
		}
	},
	async setNewToken({ commit }, newToken) {
		commit('SET_TOKEN', newToken);
	},
	async isShowFimalacPlanning({ commit, state }) {
		try {
			const { userID, token } = state;
			if (token) {
				const userId = userID;
				const data = await getValidations(4);
				if (!data.length) {
					commit('SET_FIMALAC', false);
				} else {
					const result = data.find((item) => +item.user.id === +userId);
					const type_status = result ? +result.validationLevelTs : null;
					const isValid = type_status !== null && [0, 1, 2].includes(type_status);
					commit('SET_FIMALAC', isValid);
				}
			} else {
				commit('SET_FIMALAC', false);
			}
		} catch (error) {
			commit('SET_FIMALAC', false);
		}
	},
	async getAccessValidationPlanning({ commit, state }) {
		try {
			const { userID, token } = state;
			const userId = userID;
			if (token) {
				const response = await GetPlanningValidation(userId);
				if (response) {
					let type_status = response.validationLevelTs;
					const convertStatus = !!type_status;
					commit('SET_IS_PROD', convertStatus);
				}
			} else {
				commit('SET_IS_PROD', false);
			}
		} catch (error) {
			commit('SET_IS_PROD', false);
		}
	},
	async getMyProjects({ commit, state }) {
		try {
			const projects = await myListOfProjects();
			if (projects) {
				commit('SET_MY_PROJECTS', projects);
			}
		} catch (error) {
			commit('SET_MY_PROJECTS', []);
		}
	},
	async setProfile({ commit }) {
		const prof = await myProfile();
		commit('SET_MY_PROFILE', prof);
	},
	setProject({ commit }, payload) {
		commit('SET_PROJECT', payload);
	},
	async globalLoaded({ commit, dispatch, state }) {
		try {
			if (state.token) {
				const record = await myProject();
				if (record) {
					const noTranslation = {
						userName: record.userName,
						hodName: record.hodName,
						managerName: record.managerName
					};
					commit('SET_PROJECT', record);
					commit('SET_NO_TRANSLATION', noTranslation);
					commit('SET_LOADED', true);
					dispatch('setProfile');
					dispatch('isShowFimalacPlanning');
					dispatch('getAccessValidationPlanning');
				}
			} else {
				commit('SET_PROJECT', {});
				commit('SET_NO_TRANSLATION', {});
				commit('SET_LOADED', true);
				dispatch('setProfile');
				dispatch('isShowFimalacPlanning');
				dispatch('getAccessValidationPlanning');
			}
		} catch (error) {
			console.error(error);
			commit('SET_LOADED', true);
		}
	},

	async reloadMyProject({ commit, dispatch }) {
		dispatch('globalLoaded');
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
